import { useLazyQuery, useQuery } from "@apollo/client";
import { useContext } from "react";
import { GlobalContext } from "~/context/global-context";
import { FilteredMattersInput } from "~/gql/graphql";
import { FILTER_MATTERS_QUERY } from "~/graphql/query";
import { MatterStatus } from "~/graphql/__generated_types__/globalTypes";

type BaseFilterHelperResult = {
  vars: FilteredMattersInput;
  withNewStatus: (status: MatterStatus) => FilteredMattersInput;
};
export function getBaseFilterVariables(): BaseFilterHelperResult {
  const currentUserInfo = useContext(GlobalContext);

  const baseVariables: FilteredMattersInput = {
    filter: {
      status: [MatterStatus.Draft],
      template: false,
    },
    orderBy: [
      {
        updatedAt: "desc",
      },
    ],
    offset: 0,
    limit: 10,
  };

  return {
    vars: {
      ...baseVariables,
      filter: {
        ...baseVariables.filter,
        ...(currentUserInfo?.currentUser?.userId && {
          createdBy: [currentUserInfo?.currentUser.userId],
        }),
      },
    },
    withNewStatus: (status: MatterStatus) => {
      const result: FilteredMattersInput = {
        ...baseVariables,
        filter: {
          ...baseVariables.filter,
          status: [status],
          ...(currentUserInfo?.currentUser?.userId && {
            createdById: { in: [currentUserInfo?.currentUser.userId] },
          }),
        },
      };

      return result;
    },
  };
}

export type FilteredMattersQueryVariables = {
  title?: string;
  clientIds?: number[];
  status?: MatterStatus[];
  updatedAt: {
    from: string;
    to: string;
  };
  createdBy?: number[];
};

export const useFilteredMattersQuery = (
  filteredMattersProps: FilteredMattersInput,
  skip?: boolean
) => {
  const queryTypes = useQuery(FILTER_MATTERS_QUERY, {
    variables: {
      input: filteredMattersProps,
    },
    skip,
    fetchPolicy: "cache-and-network",
  });

  return queryTypes;
};

export const useFilteredMattersLazyQuery = (filteredMattersProps: FilteredMattersInput) => {
  const [callFunc, { loading, data, variables, error, fetchMore: fetchMoreRaw }] = useLazyQuery(
    FILTER_MATTERS_QUERY,
    {
      variables: {
        input: filteredMattersProps,
      },
      fetchPolicy: "cache-and-network",
    }
  );

  const total = data?.filteredMatters?.count || 0;

  return {
    callFunc,
    loading,
    data,
    variables,
    error,
    total,
    fetchMore: () => {
      fetchMoreRaw &&
        fetchMoreRaw({
          variables: {
            input: {
              ...variables?.input,
              offset: data?.filteredMatters?.matters?.length || 0,
              limit: 10,
            },
          },
          updateQuery(prev, { fetchMoreResult }) {
            if (!fetchMoreResult) return prev;
            return {
              ...prev,
              filteredMatters: {
                ...prev.filteredMatters,
                count: fetchMoreResult.filteredMatters.count,
                matters: [
                  ...prev.filteredMatters.matters,
                  ...fetchMoreResult.filteredMatters.matters,
                ],
              },
            };
          },
        });
    },
  };
};
